import { Stack, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Component
import WhiteBoxContent from 'src/components/common/WhiteBoxContent';
import Table from 'src/components/Table';
import { renderNumberOrDefault, renderTextOrDefault, renderValueNumberTrade } from 'src/utils/helpers/stringHelpers';

import useCreditFlow from 'src/hooks/useCreditFlow';
import { useQueryParams } from 'src/hooks/useQueryParam';
import { useQueryAverageUnitPriceChart, useQueryAverageUnitPriceTable } from 'src/services/queries/useQueryTradeData';
import { TypeQueryParamPortTransaction } from 'src/utils/typings/dto/tradeData';
import { useTradeDataSortStore } from 'src/zustand/useTradeDataSortStore';

import { isEmpty } from 'lodash';
import CompanyInfoNoData from 'src/features/CompanyDetail/Common/CompanyInfoNoData';
import FilterChart from 'src/features/TradeData/Common/FilterChart';
import { FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { LIST_ACTIONS, LIST_FEATURES } from 'src/utils/constants/featuresPoint';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import LineChart from 'src/components/HighChart/LineChart';
import dayjs from 'dayjs';

const AverageUnitPrice = () => {
  const { t } = useTranslation();
  const queryParams = useQueryParams();
  const { setSort, averageUnitPriceSortParams } = useTradeDataSortStore();

  const [acceptCreditPayment, setAcceptCreditPayment] = useState(true);
  const [selectedRangeTime, setSelectedRangeTime] = useState({
    fromTime: queryParams.start_date,
    toTime: queryParams.end_date,
  } as {
    fromTime: string;
    toTime: string;
  });

  const tableSortParams = averageUnitPriceSortParams;

  const [tablePaging, setTablePaging] = useState({
    page: 0,
    page_size: 10,
  });

  const chartRequestPayload: TypeQueryParamPortTransaction = useMemo(() => {
    return {
      ...queryParams,
      limit: 20,
      offset: 0,
      start_date: selectedRangeTime.fromTime,
      end_date: selectedRangeTime.toTime,
      sort_by: tableSortParams?.sortBy,
      order: tableSortParams?.sort,
    };
  }, [queryParams, selectedRangeTime, tableSortParams]);

  const tableRequestPayload: TypeQueryParamPortTransaction = useMemo(() => {
    return {
      ...chartRequestPayload,
      limit: tablePaging.page_size,
      offset: tablePaging.page,
    };
  }, [chartRequestPayload, tablePaging]);

  const {
    data: chartData,
    dataMapping: chartDataMapping,
    isFetching: isFetchingChartData,
  } = useQueryAverageUnitPriceChart(acceptCreditPayment ? chartRequestPayload : undefined);

  const {
    data: tableData,
    dataMapping: tableDataMapping,
    isFetching: isFetchingTableData,
  } = useQueryAverageUnitPriceTable(acceptCreditPayment ? tableRequestPayload : undefined);

  const lineChartData = getArrayDefault(chartDataMapping);
  const tableListData = getArrayDefault(tableDataMapping);

  const [shouldShowBillableDataLoading] = useCreditFlow({
    response: chartData,
    featureInfo: {
      featureId: LIST_FEATURES.INTERNAL_TRADE_DATA_UNIT_PRICES,
      action: LIST_ACTIONS.VIEW,
    },
    acceptCreditPayment,
    onSetAcceptCreditPayment: (value) => setAcceptCreditPayment(value),
  });

  const lineChart = useMemo(() => {
    const unitData = chartData?.unit;
    const lineChartYData = lineChartData.map((item: IBuyerSupplierUnitPriceMonthly) => [item?.avg_unit_price]);
    const lineChartXData = lineChartData.map((item: IMarketAnalytics) =>
      dayjs(item?.month, FORMAT_DATE_TIME.yyyy_mm).format(FORMAT_DATE_TIME.MONTH_YEAR),
    );

    return (
      <LineChart
        loading={shouldShowBillableDataLoading || isFetchingChartData}
        data={lineChartYData}
        dataPropertyKeys={['y', 'custom.number_of_suppliers', 'custom.number_of_buyers']}
        tooltipFormatter={function (this) {
          return `<b>${dayjs(this.x, FORMAT_DATE_TIME.MONTH_YEAR).format(FORMAT_DATE_TIME.LONG_MONTH_YEAR)}</b><br>
      <span style="color: white">Average Unit Price: <b>${renderNumberOrDefault(this.y)} ${unitData}</b></span><br>`;
        }}
        categories={lineChartXData}
      />
    );
  }, [lineChartData, chartData?.unit]);

  const tableConfigColumn: ConfigCell<IBuyerSupplierUnitPriceMonthly>[] = [
    {
      disablePadding: false,
      // disableSort: true,
      id: 'month',
      label: 'Month',
      cell: (data: IBuyerSupplierUnitPriceMonthly) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderTextOrDefault(data.month)}
          </Typography>
        );
      },
    },
    {
      disablePadding: false,
      id: 'avg_unit_price',
      label: 'Avg Price',
      cell: (data: IBuyerSupplierUnitPriceMonthly) => (
        <Typography variant="body2" color="text.secondary">
          {renderNumberOrDefault(data.avg_unit_price)} ({tableData?.unit})
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'total_volume',
      label: 'Total Volume',
      cell: (data: IBuyerSupplierUnitPriceMonthly) => (
        <Typography variant="body2" color="text.secondary">
          {renderValueNumberTrade(data.total_volume)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'transactions',
      label: 'Sample Size',
      cell: (data: IBuyerSupplierUnitPriceMonthly) => (
        <Typography variant="body2" color="text.secondary">
          {renderNumberOrDefault(data.transactions)}
        </Typography>
      ),
    },
  ];

  if (!isFetchingChartData && !isFetchingTableData && isEmpty(lineChartData) && isEmpty(tableListData)) {
    return (
      <CompanyInfoNoData
        title={t('trade_data.common.notice_no_data.title')}
        content={<>{t('trade_data.common.notice_no_data.sub_title')}</>}
      />
    );
  }

  return (
    <Stack direction="column" spacing={3}>
      <WhiteBoxContent>
        <Stack direction="column" spacing={3}>
          <FilterChart
            onSelectTimeRange={({ fromTime, toTime }) => {
              setSelectedRangeTime({
                fromTime: fromTime,
                toTime: toTime,
              });
            }}
            title="Average Unit Price"
            selectedRangeTime={{
              fromTime: chartRequestPayload.start_date || '',
              toTime: chartRequestPayload.end_date || '',
            }}
          />
          {lineChart}
        </Stack>
      </WhiteBoxContent>
      <WhiteBoxContent>
        <Stack direction="column" spacing={3}>
          <Typography display={'flex'} justifyContent={'space-between'} textAlign="left" variant="h5">
            Average Unit Price Data
          </Typography>
          <Table
            maxRow={50}
            handleSort={(sortData) => {
              setSort('averageUnitPriceSortParams', sortData);
            }}
            onChangePage={(value) => {
              setTablePaging({ ...tablePaging, page: value });
            }}
            onChangePageSize={(value) => setTablePaging({ page: 0, page_size: value })}
            loading={shouldShowBillableDataLoading || isFetchingTableData}
            page={tableData.page}
            pageSize={tableData.page_size}
            totalRecords={tableData.total_records}
            dataKey="month"
            data={tableListData}
            configColumn={tableConfigColumn}
          />
        </Stack>
      </WhiteBoxContent>
    </Stack>
  );
};

export default AverageUnitPrice;
